import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Clear authentication on page refresh/reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear authentication data when page is about to be refreshed/closed
      localStorage.removeItem('token');
      sessionStorage.removeItem('user');
    };

    const handlePageShow = (event) => {
      // If page is loaded from cache (back/forward navigation), also clear auth
      if (event.persisted) {
        logout();
      }
    };

    // Add event listeners for page refresh/unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pageshow', handlePageShow);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Check if this is a fresh page load (not a navigation within the app)
  useEffect(() => {
    const checkSessionValidity = () => {
      const activeSession = sessionStorage.getItem('activeSession');
      const hasToken = localStorage.getItem('token');
      
      // If we have a token but no active session, it means the page was refreshed
      if (hasToken && !activeSession) {
        console.log('Page refresh detected (no active session), logging out user');
        logout();
        return true;
      }
      
      // Alternative method: check navigation type
      const isPageRefresh = performance.navigation?.type === 1 || 
                           performance.getEntriesByType('navigation')[0]?.type === 'reload';
      
      if (isPageRefresh && hasToken) {
        console.log('Page refresh detected (navigation type), logging out user');
        logout();
        return true;
      }
      
      return false;
    };

    // Check session validity on component mount
    const wasRefreshed = checkSessionValidity();
    
    // If not refreshed and we have a token, set active session
    if (!wasRefreshed && token) {
      sessionStorage.setItem('activeSession', 'true');
    }
  }, [token]); // Added token dependency

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verify token on app load (only if session is valid)
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      // Check if we have an active session
      const activeSession = sessionStorage.getItem('activeSession');
      
      if (!activeSession) {
        // No active session, don't verify token
        logout();
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/auth/verify`);
        setUser(response.data.user);
      } catch (error) {
        console.error('Token verification failed:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    // Set a session flag to track active session
    sessionStorage.setItem('activeSession', 'true');
    sessionStorage.setItem('loginTime', Date.now().toString());
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    sessionStorage.removeItem('activeSession');
    sessionStorage.removeItem('loginTime');
    sessionStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const googleLogin = async (googleData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google`, {
        email: googleData.email,
        name: googleData.name,
        googleId: googleData.sub,
        picture: googleData.picture,
        emailVerified: googleData.email_verified
      });

      login(response.data.user, response.data.token);
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const usernameLogin = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password
      });

      login(response.data.user, response.data.token);
      return { success: true };
    } catch (error) {
      console.error('Username login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const createCredentials = async (username, password) => {
    try {
      await axios.post(`${API_BASE_URL}/auth/create-credentials`, {
        username,
        password
      });

      // Update user object with new username
      setUser(prev => ({ ...prev, username }));
      return { success: true };
    } catch (error) {
      console.error('Create credentials error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to create credentials' 
      };
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    token,
    login,
    logout,
    updateUser,
    googleLogin,
    usernameLogin,
    createCredentials
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};